import { call, put, all, takeLatest, select } from 'redux-saga/effects';
import {
    getDiscoverySuccess,
    getDiscoveryFail,
    searchDiscoverySuccess,
    searchDiscoveryFail,
    filterSuccess,
    filterFail,
} from './actions';
import { globalErrorHandler } from '../error/saga';
import {
    Discovery,
    IFilterPayload,
    IFilterResponse,
    IGetDiscoveryPayload,
    ISearchPayload,
    ISetFilter,
    types,
} from './types';
import { api } from '../../configurations/api';
import { httpRequest } from '../types';
import { filterUrl, getDiscoveryUrl, searchDiscoveryUrl } from '../../configurations/api/url';
import { Action } from 'redux';
import { RootSearchResponse } from './search.types';
import { RootState } from '../root-reducer';
import { FilterItem } from '../../data/filters/discovery.data';
import { MD5, enc } from 'crypto-js';
import { en_config } from '../../config';
import { setCache } from '../cache/action';
import { getCacheByType, getCacheDuration, getNotUniqueCacheByKey, isUseCacheEnabled } from '../cache/saga';
import { CACHE_TYPE, CacheValue, NotUniqueCacheValue } from '../cache/types';
import { parseDiscoverySearchResult } from '../../utils/responseProcessor';
import { DiscoverySearchUnparsedResponse, ISearchUgcCampaign } from '../../types/global/generic.types';
import { projectActParse } from '../../utils/reponseProcessorII';
import { IProject } from '../../types/global/helper';
import { SentryCapture } from '../../analytics/Sentry';
import { isEmpty } from '../../utils/lodash';

function buildContestHomeUrl(base: string, query: Record<string, string>): string {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
        if (v != null && v !== '') sp.append(k, v);
    }
    const qs = sp.toString();
    return qs ? `${base}?${qs}` : base;
}

function normalizeHomeQuery(query: IGetDiscoveryPayload['query']): Record<string, string> {
    const out: Record<string, string> = {};
    if (!query) return out;
    for (const [k, v] of Object.entries(query)) {
        if (v != null && v !== '') out[k] = String(v);
    }
    return out;
}

function* getDiscovery(action: any): any {
    const { resolve, reject, payload } = action;
    const { query: rawQuery, contentOnly } = (payload || {}) as IGetDiscoveryPayload;
    const query = normalizeHomeQuery(rawQuery);
    const hasQuery = Object.keys(query).length > 0;
    const skipCacheRead = hasQuery || !!contentOnly;

    const defaultUseCache = yield* isUseCacheEnabled();
    const defaultCacheDuration = yield* getCacheDuration();

    let initialResult: any = null;

    if (!skipCacheRead) {
        const cache: CacheValue = yield* getCacheByType(CACHE_TYPE.DISCOVERY);
        if (
            cache &&
            cache.key &&
            !isEmpty(cache.value) &&
            defaultUseCache &&
            ((Date.now() - Number(cache.key)) as unknown as number) < defaultCacheDuration
        ) {
            initialResult = cache.value;
        }
    }

    const cachedRows = Array.isArray(initialResult) ? initialResult : initialResult?.data;

    if (!skipCacheRead && cachedRows && cachedRows.length > 0 && defaultUseCache) {
        const successPayload: Discovery = { discovery: cachedRows, isCache: true };
        if (!Array.isArray(initialResult)) {
            successPayload.filters = initialResult.filters ?? null;
        }
        yield put(getDiscoverySuccess(successPayload));
        resolve?.(cachedRows);
        return;
    }

    try {
        const url = buildContestHomeUrl(getDiscoveryUrl, query);
        const response = yield call(api, url, httpRequest.GET, null, 0, 0, false, (e: any) => {
            console.log(e);
        });
        const data = Array.isArray(response) ? response : response?.data;
        const filters = Array.isArray(response) ? null : response?.filters ?? null;
        if (!Array.isArray(data)) {
            throw new Error('Invalid discovery response');
        }
        yield put(getDiscoverySuccess({ discovery: data, filters, isCache: false }));

        if (!hasQuery && defaultUseCache) {
            yield put(
                setCache({
                    key: Date.now(),
                    value: { data, filters },
                    type: CACHE_TYPE.DISCOVERY,
                    isUnique: true,
                }),
            );
        }
        resolve?.(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getDiscoveryFail(error));
        yield call(globalErrorHandler, error);
        reject?.(error);
    }
}

function* initiateSearch({ payload }: { payload: ISearchPayload }): any {
    const defaultUseCache = yield* isUseCacheEnabled();
    let initialResult: any = null;

    const cacheKey = [payload.searchTerm, '', payload.location?.country_code || '', payload.location?.place || ''].join(
        '|',
    );
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.PROJECT, cacheKey);
    if (cache && cache.key) {
        initialResult = cache.value;
    }

    /** Check if this searchterm has already yielded any result then return that same response */
    if (initialResult && defaultUseCache) {
        const { parsedResult, unParsedResult } = initialResult;
        yield put(
            searchDiscoverySuccess({
                searchTerm: payload.searchTerm,
                searchResult: parsedResult,
                allSearchResults: unParsedResult,
            }),
        ); // This allows us cache the search term to return it if the search has already occured
    } else {
        try {
            const response = yield call(api, searchDiscoveryUrl, httpRequest.POST, payload, 0, 0);
            const { data } = response as RootSearchResponse;
            const results = parseDiscoverySearchResult(data);
            /** Also fetching all results so we can display that on teh view all page */
            const { parsedResult, unParsedResult } = results;
            yield put(
                searchDiscoverySuccess({
                    searchTerm: payload.searchTerm,
                    searchResult: parsedResult,
                    allSearchResults: unParsedResult,
                }),
            ); // This allows us cache the search term to return it if the search has already occured
            yield put(
                setCache({
                    key: cacheKey,
                    value: results,
                    type: CACHE_TYPE.SEARCH,
                    isUnique: false,
                }),
            );
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(searchDiscoveryFail(error));
            yield call(globalErrorHandler, error);
        }
    }
}

const parseFilterPayload = (data: { [key: number]: FilterItem }) => {
    const parsedObject: any = {};
    Object.keys(data).forEach((d: any) => {
        parsedObject[data[d].field] = data[d].value;
    });
    return parsedObject;
};

const getConsistentHash = (obj: any) => {
    const objStr = JSON.stringify(obj);
    const hashObj = MD5(objStr).toString(enc.Hex);
    return hashObj;
};

function* filter({ payload }: { payload: ISetFilter }): any {
    let initialResult: any = null;
    const state: RootState = yield select();
    const { selectedFilterCategory, selectedFiltersValues } = state.appConfigurator;
    const defaultUseCache = yield* isUseCacheEnabled();
    const dataPayload: IFilterPayload = {
        category: payload.customCategory ?? selectedFilterCategory,
        params: parseFilterPayload(selectedFiltersValues as any),
        limit: en_config.RESULT_LIMIT,
        page: payload.page || 0,
    };

    const cacheKey = getConsistentHash(dataPayload);
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.FILTER, cacheKey);

    if (cache && cache.key) {
        initialResult = cache.value;
    }

    if (initialResult && defaultUseCache) {
        yield put(filterSuccess(initialResult));
    } else {
        try {
            const parsedFilterResult: DiscoverySearchUnparsedResponse = {
                acts: [],
                contests: [],
                projects: [],
                users: [],
                suggestion: [],
                auditions: [],
                events: [],
                company: [],
                campaigns: [],
            };
            const response: IFilterResponse = yield call(api, filterUrl, httpRequest.POST, dataPayload, 0, 0);
            const { data } = response;
            response.currentPage = payload.page;
            switch (selectedFilterCategory) {
                case 'contest':
                    parsedFilterResult.contests = data as any;
                    break;
                case 'campaigns':
                    parsedFilterResult.campaigns = (Array.isArray(data) ? data : []) as ISearchUgcCampaign[];
                    break;
                case 'video':
                    const { acts, projects } = projectActParse(data as IProject[]);
                    parsedFilterResult.projects = projects;
                    parsedFilterResult.acts = acts;
                    break;
                case 'talent':
                    // const parsedTalent = parseGenericCollection(data, genericParseSingleDocument);
                    parsedFilterResult.users = data as any;
                    break;
                default:
                    break;
            }
            response.result = parsedFilterResult;
            yield put(filterSuccess(response));
            yield put(
                setCache({
                    key: cacheKey,
                    value: response,
                    type: CACHE_TYPE.FILTER,
                    isUnique: false,
                }),
            );
        } catch (error: any) {
            SentryCapture(error, 'error');
            console.log(error);
            yield put(filterFail(error));
        }
    }
}

interface TaskAction extends Action, ISearchPayload {
    type: any;
    payload: ISearchPayload;
    resolve: any;
    reject: any;
}

function* getDiscoveryWatcher() {
    yield takeLatest(types.GET_DISCOVERY, getDiscovery);
}

function* initiateSearchWatcher() {
    yield takeLatest<TaskAction>(types.SEARCH_DISCOVERY, initiateSearch);
}

function* filterWacther() {
    yield takeLatest<TaskAction>(types.FILTER, filter);
}

export default function* discoverySaga() {
    yield all([getDiscoveryWatcher(), initiateSearchWatcher(), filterWacther()]);
}
